import { sendEmailTimeTrackingRequest, TimeLogType, TimelogBillable } from './../../entities/time_log/TimeLogResponse';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { TimeLogAPI } from '@business/api/time_log';
import { ManualTimeLogRequest, TimeLogId, TimeLogResponse, TimeLogStatus, TimeLogFilterType } from '@business/entities/time_log';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';
import { OrgId, TimeTrackingInfoResponse } from '@business/entities/organization';

export class TimeLogBloc extends BaseBloc {
  private readonly ltRepo = new Repository<TimeLogResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.ltRepo.items$;
  readonly openUserItems$ = (userId?: UserId) =>
    this.ltRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(i => i.user.id === userId && i.status === TimeLogStatus.started),
        ),
      ),
    );

  readonly orgOpenTimers = (orgId: OrgId) => {
    return this.ltRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(i => i.orgId === orgId && i.status === TimeLogStatus.started),
        ),
      ),
    );
  };

  readonly selectItem = this.ltRepo.selectItem;

  onReset() {
    this.ltRepo.reset();
  }

  fetchSingleTimelog = (id: TimeLogId) => {
    return TimeLogAPI.fetchSingleTimelog(id).pipe(this.ltRepo.ops.upsertOne(item => ({ item })));
  };

  fetchTimeLogs = (type: string, objectId: string) => {
    return TimeLogAPI.fetchTimeLogs(type, objectId).pipe(
      this.ltRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_time_log_error') }),
    );
  };

  fetchOpenTimeLogs = (filterType: TimeLogFilterType, filterId: string) => {
    return TimeLogAPI.fetchOpenTimeLogs(filterType, filterId).pipe(
      this.ltRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_time_log_error') }),
    );
  };

  createManualTime = (type: string, objectId: string, data: ManualTimeLogRequest, parentId?: string, parentType?: TimeLogType) => {
    return TimeLogAPI.createManualTime(type, objectId, data, parentId, parentType).pipe(
      this.ltRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('manual_time_log'),
      }),
    );
  };

  updateManualTime = (id: TimeLogId, data: ManualTimeLogRequest) => {
    return TimeLogAPI.updateManualTime(id, data).pipe(
      this.ltRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_manual_time_log'),
      }),
    );
  };

  startTimeLog = (type: string, objectId: string, extraInfo?: any, parentId?: string, parentType?: TimeLogType, billable?: TimelogBillable) => {
    return TimeLogAPI.startTimeLog(type, objectId, extraInfo, parentId, parentType, billable).pipe(
      this.ltRepo.ops.addOne(item => item),
      handleMessage({
        error: requestMessage('time_tracker_start_error'),
      }),
    );
  };

  stopTimeLog = (id: TimeLogId, data?: TimeTrackingInfoResponse) => {
    return TimeLogAPI.stopTimeLog(id, data).pipe(
      this.ltRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('stop_time_log'),
      }),
    );
  };

  deleteTimeLog = (id: TimeLogId) => {
    return showDeleteConfirmation('Delete Time Log', 'Do you really want to remove this time log?').pipe(
      switchMap(() => TimeLogAPI.deleteTimeLog(id)),
      this.ltRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_time_log'),
      }),
    );
  };

  getSupportHourInfo = (orgId: OrgId) => {
    return TimeLogAPI.getSupportHourInfo(orgId);
  };

  stopTimer = () => {
    return TimeLogAPI.stopTimer().pipe();
  };
}
